// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTriggerMenu.tsx
 * @input Uses React, usePopover, SearchSource
 * @output Exports useTriggerMenu hook for trigger-based menus in contentEditable
 * @position Internal hook; consumed by ChatComposerInput
 *
 * Detects trigger characters (@ / etc.) typed inside a contentEditable,
 * opens a popover at the cursor position with filtered items, handles
 * keyboard navigation, and inserts tokens or text on selection.
 *
 * Reuses SearchSource from Typeahead for async/sync search with
 * cancel() support and debounce.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Chat/ChatComposerInput.tsx
 * - /packages/core/src/Chat/index.ts
 */
import { useState, useCallback, useRef, useEffect, useId } from 'react';
import * as stylex from '@stylexjs/stylex';
import { usePopover } from "../Popover/usePopover.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typeScaleVars, typographyVars } from "../theme/tokens.stylex.js";
import { mergeProps, groupItems } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  popoverSurface: {
    k7Eaqz: "xfb3i0g",
    $$css: true
  },
  popoverGap: {
    keoZOQ: "xcsaf9d",
    k1K539: "x14cgwvg",
    $$css: true
  }
};

// =============================================================================
// Helpers
// =============================================================================

function getTextBeforeCursor(editable) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!range.collapsed) {
    return null;
  }
  if (!editable.contains(range.startContainer)) {
    return null;
  }
  const node = range.startContainer;
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? '').slice(0, range.startOffset);
  }
  return null;
}
function findActiveTrigger(textBeforeCursor, triggers) {
  for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
    const char = textBeforeCursor[i];
    if (char === ' ' || char === '\n') {
      return null;
    }
    for (const trigger of triggers) {
      if (char === trigger.character) {
        const prevChar = i > 0 ? textBeforeCursor[i - 1] : null;
        if (prevChar === null || prevChar === ' ' || prevChar === '\n') {
          const query = textBeforeCursor.slice(i + 1);
          return {
            trigger,
            query,
            triggerStart: i
          };
        }
      }
    }
  }
  return null;
}
function deleteTriggerText(editable, triggerStart) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }
  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) {
    return;
  }
  const text = node.textContent ?? '';
  const cursorOffset = range.startOffset;
  const before = text.slice(0, triggerStart);
  const after = text.slice(cursorOffset);
  node.textContent = before + after;
  const newRange = document.createRange();
  newRange.setStart(node, triggerStart);
  newRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(newRange);
}

// =============================================================================
// Hook
// =============================================================================

export function useTriggerMenu(options) {
  const t = useTranslator();
  const {
    triggers,
    editableRef,
    onInsertToken,
    onInsertText,
    onEmitChange,
    debounceMs = 150
  } = options;
  const listboxId = useId();
  const [state, setState] = useState({
    isActive: false,
    activeTrigger: null,
    query: '',
    items: [],
    highlightedIndex: 0,
    isLoading: false
  });
  const triggerStartRef = useRef(-1);
  const searchTimeoutRef = useRef(null);
  const anchorSpanRef = useRef(null);
  const removeAnchorSpan = useCallback(() => {
    if (anchorSpanRef.current) {
      anchorSpanRef.current.remove();
      anchorSpanRef.current = null;
    }
  }, []);
  const popover = usePopover({
    onHide: useCallback(() => {
      removeAnchorSpan();
      setState(prev => ({
        ...prev,
        isActive: false,
        activeTrigger: null,
        query: '',
        items: [],
        highlightedIndex: 0,
        isLoading: false
      }));
    }, [removeAnchorSpan]),
    hasLightDismiss: true,
    hasCloseButton: false,
    hasAutoFocus: false,
    // The popup's own role="listbox" is the exposed semantics; focus stays in
    // the contenteditable composer, so a modal dialog wrapper is incorrect.
    role: 'none'
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      removeAnchorSpan();
    };
  }, [removeAnchorSpan]);
  const reset = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    // Cancel any in-flight search on the active trigger's searchSource
    const trigger = state.activeTrigger;
    if (trigger?.searchSource) {
      trigger.searchSource.cancel?.();
    }
    removeAnchorSpan();
    popover.hide();
    triggerStartRef.current = -1;
  }, [popover, state.activeTrigger, removeAnchorSpan]);

  // Anchor the popover to the cursor position (not the entire input).
  // We append a zero-size span to document.body positioned at the cursor
  // rect — outside the contentEditable to avoid splitting text nodes.
  const setAnchor = useCallback(() => {
    const editable = editableRef.current;
    if (!editable) {
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      popover.triggerRef(editable);
      return;
    }
    removeAnchorSpan();
    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    // getBoundingClientRect is unavailable in some environments (e.g. jsdom)
    const rect = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : null;

    // Fall back to the editable when layout info is unavailable
    if (!rect || rect.width === 0 && rect.height === 0 && rect.top === 0 && rect.left === 0) {
      popover.triggerRef(editable);
      return;
    }
    const span = document.createElement('span');
    span.setAttribute('aria-hidden', 'true');
    span.setAttribute('data-astryx-trigger-anchor', '');
    span.style.position = 'fixed';
    span.style.left = `${rect.left}px`;
    span.style.top = `${rect.top}px`;
    span.style.width = `${Math.max(rect.width, 1)}px`;
    span.style.height = `${rect.height}px`;
    span.style.pointerEvents = 'none';
    span.style.opacity = '0';
    document.body.appendChild(span);
    anchorSpanRef.current = span;
    popover.triggerRef(span);
  }, [editableRef, popover, removeAnchorSpan]);
  const searchItems = useCallback((trigger, query) => {
    // Clear any pending debounce
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    const doSearch = () => {
      if (trigger.searchSource) {
        // Use SearchSource \u2014 cancel previous, then search
        trigger.searchSource.cancel?.();
        setState(prev => ({
          ...prev,
          isLoading: true
        }));
        const result = trigger.searchSource.search(query);
        Promise.resolve(result).then(items => {
          setState(prev => ({
            ...prev,
            items,
            highlightedIndex: items.length > 0 ? 0 : -1,
            isLoading: false
          }));
        }, () => {
          setState(prev => ({
            ...prev,
            items: [],
            highlightedIndex: -1,
            isLoading: false
          }));
        });
      }
    };

    // Debounce async sources, immediate for sync
    if (trigger.searchSource) {
      // Check if search is likely sync (returns array, not promise)
      const testResult = trigger.searchSource.search('');
      const isAsync = testResult instanceof Promise;
      if (isAsync && debounceMs > 0) {
        setState(prev => ({
          ...prev,
          isLoading: true
        }));
        searchTimeoutRef.current = setTimeout(doSearch, debounceMs);
      } else {
        doSearch();
      }
    }
  }, [debounceMs]);
  const selectItem = useCallback(item => {
    const trigger = state.activeTrigger;
    if (!trigger) {
      return;
    }
    const editable = editableRef.current;
    if (!editable) {
      return;
    }

    // Clean up anchor span before modifying DOM — if the span were
    // inside the editable it would split text nodes and break offsets.
    // With the body-appended approach this is just bookkeeping.
    removeAnchorSpan();
    deleteTriggerText(editable, triggerStartRef.current);
    const result = trigger.onSelect(item);
    if (typeof result === 'string') {
      onInsertText(result);
    } else {
      onInsertToken(result);
    }
    onEmitChange();
    reset();
  }, [state.activeTrigger, editableRef, removeAnchorSpan, onInsertText, onInsertToken, onEmitChange, reset]);
  const handleInput = useCallback(() => {
    if (!triggers || triggers.length === 0) {
      return;
    }
    const editable = editableRef.current;
    if (!editable) {
      return;
    }
    const textBefore = getTextBeforeCursor(editable);
    if (textBefore === null) {
      if (state.isActive) {
        reset();
      }
      return;
    }
    const found = findActiveTrigger(textBefore, triggers);
    if (!found) {
      if (state.isActive) {
        reset();
      }
      return;
    }
    const {
      trigger,
      query,
      triggerStart
    } = found;
    if (!state.isActive || state.activeTrigger !== trigger) {
      triggerStartRef.current = triggerStart;
      setState(prev => ({
        ...prev,
        isActive: true,
        activeTrigger: trigger,
        query,
        highlightedIndex: 0
      }));
      setAnchor();
      searchItems(trigger, query);
      popover.show();
    } else if (state.query !== query) {
      setState(prev => ({
        ...prev,
        query
      }));
      searchItems(trigger, query);
    }
  }, [triggers, editableRef, state.isActive, state.activeTrigger, state.query, reset, setAnchor, searchItems, popover]);
  const handleKeyDown = useCallback(e => {
    if (!state.isActive || !popover.isOpen) {
      return false;
    }
    switch (e.key) {
      case 'ArrowDown':
        {
          e.preventDefault();
          setState(prev => ({
            ...prev,
            highlightedIndex: prev.highlightedIndex < prev.items.length - 1 ? prev.highlightedIndex + 1 : 0
          }));
          return true;
        }
      case 'ArrowUp':
        {
          e.preventDefault();
          setState(prev => ({
            ...prev,
            highlightedIndex: prev.highlightedIndex > 0 ? prev.highlightedIndex - 1 : prev.items.length - 1
          }));
          return true;
        }
      case 'Enter':
      case 'Tab':
        {
          if (state.highlightedIndex >= 0 && state.highlightedIndex < state.items.length) {
            e.preventDefault();
            selectItem(state.items[state.highlightedIndex]);
            return true;
          }
          return false;
        }
      case 'Escape':
        {
          e.preventDefault();
          reset();
          return true;
        }
      default:
        return false;
    }
  }, [state.isActive, state.highlightedIndex, state.items, popover.isOpen, selectItem, reset]);
  const getItemId = useCallback(index => `${listboxId}-option-${index}`, [listboxId]);

  // Scroll highlighted item into view on keyboard navigation
  useEffect(() => {
    if (!popover.isOpen || state.highlightedIndex < 0) {
      return;
    }
    const el = document.getElementById(getItemId(state.highlightedIndex));
    el?.scrollIntoView({
      block: 'nearest'
    });
  }, [state.highlightedIndex, popover.isOpen, getItemId]);

  // ARIA props for the editable element. Combobox attributes
  // (aria-expanded/haspopup/controls/activedescendant) are only valid on
  // role="combobox", so we only switch to that role — and only emit those
  // attributes — when triggers are actually configured. With no triggers the
  // element stays a plain role="textbox".
  const hasTriggers = (triggers?.length ?? 0) > 0;
  const ariaProps = !hasTriggers ? {
    role: 'textbox'
  } : state.isActive && popover.isOpen ? {
    role: 'combobox',
    'aria-expanded': true,
    'aria-controls': listboxId,
    'aria-activedescendant': state.highlightedIndex >= 0 ? getItemId(state.highlightedIndex) : undefined,
    'aria-haspopup': 'listbox'
  } : {
    role: 'combobox',
    'aria-expanded': false,
    'aria-haspopup': 'listbox'
  };
  const renderMenu = useCallback(() => {
    const trigger = state.activeTrigger;
    const emptyText = trigger?.emptySearchResultsText ?? 'No results';
    const loadingText = trigger?.loadingText ?? 'Searching\u2026';
    let listContent;
    if (state.isLoading) {
      listContent = /*#__PURE__*/_jsx("div", {
        role: "status",
        ...{
          className: "x1b2ylru x2b8uid x141an7d xv1l7n4"
        },
        children: loadingText
      });
    } else if (state.items.length === 0 && state.isActive) {
      listContent = /*#__PURE__*/_jsx("div", {
        ...{
          className: "x1b2ylru x2b8uid x141an7d xv1l7n4"
        },
        children: emptyText
      });
    } else {
      const groups = groupItems(state.items);
      let flatIndex = 0;
      listContent = groups.map(group => {
        const groupItems = group.items.map(item => {
          const idx = flatIndex++;
          return /*#__PURE__*/_jsx("div", {
            id: getItemId(idx),
            role: "option",
            "aria-selected": idx === state.highlightedIndex,
            tabIndex: -1,
            onMouseDown: e => {
              e.preventDefault(); // Keep focus in the editable
              selectItem(item);
            },
            onMouseEnter: () => setState(prev => ({
              ...prev,
              highlightedIndex: idx
            })),
            ...{
              0: {
                className: "x9f619 x78zum5 x6s0dn4 xh8yej3 xlsj2fj xh6dtrn x1ypdohk x1a2a7pz xjbqb8w x1yc453h x9ynric xjm74w1 xw6l6zx x1tgivj0"
              },
              1: {
                className: "x9f619 x78zum5 x6s0dn4 xh8yej3 xlsj2fj xh6dtrn x1ypdohk x1a2a7pz x1yc453h x9ynric xjm74w1 xw6l6zx x1tgivj0 x1lmrjuc"
              }
            }[!!(idx === state.highlightedIndex) << 0],
            children: trigger?.renderItem ? trigger.renderItem(item) : /*#__PURE__*/_jsx("span", {
              ...{
                className: "x98rzlu xeuugli xb3r6kr xlyipyv xuxw1ft"
              },
              children: item.label
            })
          }, item.id);
        });
        if (group.heading) {
          return /*#__PURE__*/_jsxs("div", {
            role: "group",
            "aria-label": group.heading,
            children: [/*#__PURE__*/_jsx("div", {
              "aria-hidden": "true",
              ...{
                className: "xf314gf x1xye8es xy143xn x141an7d x1ltkj2j xv1l7n4 x87ps6o"
              },
              children: group.heading
            }), groupItems]
          }, `group-${group.heading}`);
        }
        return groupItems;
      });
    }
    return popover.render(/*#__PURE__*/_jsx("div", {
      id: listboxId,
      role: "listbox",
      "aria-label": trigger?.menuLabel ?? t('@astryx.chatTriggerMenu.suggestions'),
      ...mergeProps(themeProps('trigger-menu'), {
        className: "x9f619 x1xiyphd x1odjw0f x9epnlk xfb3i0g"
      }),
      children: listContent
    }), {
      placement: 'above',
      alignment: 'start',
      xstyle: [styles.popoverSurface, styles.popoverGap]
    });
  }, [popover, listboxId, state, selectItem, getItemId, t]);
  return {
    state,
    handleInput,
    handleKeyDown,
    renderMenu,
    reset,
    ariaProps
  };
}